import { roomLib } from "@agentcall/core/lib"
import type { PropertyRes, RoomRes } from "@agentcall/core/res"
import { planMasterType } from "@agentcall/core/type"
import { useEffect, useMemo, useState } from "react"
import { Plus, Search } from "react-feather"
import { Link, useNavigate } from "react-router-dom"
import { EchoesEditGuard } from "@/components/echoes/EchoesEditGuard"
import { PlanBadge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button/button"
import { EmptyCard, RoomListCard } from "@/components/ui/card/card"
import { CustomPagination } from "@/components/ui/custom-pagination"
import { Input } from "@/components/ui/input/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select/select"
import { useIsEchoesOwner } from "@/store/session"

const ROOMS_PER_PAGE = 10

const ALL_PLANS_VALUE = "all"

export function RoomList({ property }: { property?: PropertyRes | null }) {
  const [currentPage, setCurrentPage] = useState(1)
  const [roomNameInput, setRoomNameInput] = useState("")
  const [roomNameQuery, setRoomNameQuery] = useState("")
  const [planFilterInput, setPlanFilterInput] = useState(ALL_PLANS_VALUE)
  const [planFilterQuery, setPlanFilterQuery] = useState(ALL_PLANS_VALUE)
  // 加入できるプランと同じく、Echoes 連携オーナーはエコーズプラン、それ以外は従来プランのみ表示する。
  const isEchoesOwner = useIsEchoesOwner()
  const filterablePlanMasters = planMasterType.PLAN_MASTER_IDs.filter(
    (planMaster) => planMasterType.isEchoesPlanMasterId(planMaster.id) === isEchoesOwner
  )
  const navigate = useNavigate()
  const propertyId = property?.id
  const isValidPropertyId = typeof propertyId === "number" && !Number.isNaN(propertyId)

  const rooms = (property?.rooms as RoomRes[] | undefined) ?? []

  const filteredRooms = useMemo(() => {
    let result = rooms

    // 部屋名でフィルタリング
    if (roomNameQuery) {
      const query = roomNameQuery.toLowerCase()
      result = result.filter((room) => room.name?.toLowerCase().includes(query))
    }

    // プランでフィルタリング
    if (planFilterQuery !== ALL_PLANS_VALUE) {
      result = result.filter((room) => {
        const currentPlan = roomLib.roomCurrentPlan(room)
        return currentPlan?.planMasterId === planFilterQuery
      })
    }

    return result
  }, [roomNameQuery, planFilterQuery, rooms])

  const totalRooms = filteredRooms.length
  const totalPages = totalRooms > 0 ? Math.ceil(totalRooms / ROOMS_PER_PAGE) : 0
  const startIndex = (currentPage - 1) * ROOMS_PER_PAGE
  const endIndex = startIndex + ROOMS_PER_PAGE
  const currentRooms = filteredRooms.slice(startIndex, endIndex)

  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages)
    }
    if (totalPages === 0 && currentPage !== 1) {
      setCurrentPage(1)
    }
  }, [currentPage, totalPages])

  const handleViewRoomDetail = (roomId: number) => {
    if (!isValidPropertyId) return
    navigate(`/properties/${propertyId}/rooms/${roomId}`)
  }

  const handleSearch = () => {
    setRoomNameQuery(roomNameInput.trim())
    setPlanFilterQuery(planFilterInput)
    setCurrentPage(1)
  }

  return (
    <>
      <div className="rounded-md border border-slate-300 bg-white p-[15px] shadow-sm">
        <div className="flex flex-col gap-2 items-end">
          <Input
            type="text"
            placeholder="部屋番号で検索"
            value={roomNameInput}
            onChange={(event) => setRoomNameInput(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                handleSearch()
              }
            }}
            startIcon={<Search className="h-4 w-4 text-slate-300" />}
            className="h-10 w-full border-slate-300 text-base placeholder:text-slate-300"
          />
          <Select value={planFilterInput} onValueChange={setPlanFilterInput}>
            <SelectTrigger className="h-10 w-full border-slate-300 text-sm font-medium text-slate-600">
              <SelectValue placeholder="全ての部屋" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_PLANS_VALUE}>全ての部屋</SelectItem>
              {filterablePlanMasters.map((planMaster) => (
                <SelectItem key={planMaster.id} value={planMaster.id}>
                  {planMaster.value}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={handleSearch} className="h-10" block>
            <Search className="h-3.5 w-3.5" />
            検索
          </Button>
        </div>
      </div>

      <div className="flex items-end justify-between mt-6">
        <div className="text-base text-primary font-semibold">{`${totalRooms}件`}</div>
        {isValidPropertyId ? (
          <EchoesEditGuard>
            <Link to={`/properties/${propertyId}/addRoom`}>
              <Button>
                <Plus className="h-3.5 w-3.5" />
                部屋の追加
              </Button>
            </Link>
          </EchoesEditGuard>
        ) : (
          <Button disabled>
            <Plus className="h-3.5 w-3.5" />
            部屋の追加
          </Button>
        )}
      </div>

      <div className="space-y-2.5 mt-3">
        {!isValidPropertyId && <EmptyCard text="物件情報が見つかりません" />}

        {isValidPropertyId && rooms.length === 0 && <EmptyCard text="登録された部屋がありません" />}

        {currentRooms.map((room) => {
          const currentTenant = roomLib.roomCurrentTenant(room)
          const currentPlan = roomLib.roomCurrentPlan(room)
          return (
            <RoomListCard
              key={room.id}
              title={room.name ?? "部屋名未設定"}
              badge={<PlanBadge plan={currentPlan} />}
              details={[
                { subtitle: "初回賃貸借契約開始日", text: currentTenant?.startDate ?? "-" },
                { subtitle: "賃貸借契約終了日", text: currentTenant?.endDate ?? "-" },
                { subtitle: "プラン利用状況", text: currentPlan?.planMaster?.name ?? "-" },
                { subtitle: "賃借人", text: currentTenant?.name ?? "-" },
              ]}
              buttonText="詳細を見る"
              onButtonClick={() => {
                if (room.id !== undefined) {
                  handleViewRoomDetail(room.id)
                }
              }}
            />
          )
        })}
      </div>

      {totalPages > 1 && (
        <CustomPagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={(page) => setCurrentPage(page)}
        />
      )}
    </>
  )
}
